import { useEffect, useRef, useState } from 'react';

import { getFileSize } from './fileSize.helper';

import type { ExportFormat, ExportTable } from './export.types';
import type { ExportScope } from './listExport.helper';

export type ExportSizeSource = { fileName: string; table: ExportTable };
export type ExportSizes = Partial<Record<ExportScope, Partial<Record<ExportFormat, number>>>>;

const FORMATS: ExportFormat[] = ['csv', 'excel'];

/**
 * Sizes in bytes of the files of each scope, generated in the browser (Excel is built, so it is not free).
 * Nothing is computed while `sources` is null, and a table already measured is not measured again.
 * A format that cannot be measured has no entry: the failure never reaches the download itself.
 */
export function useExportSizes(sources: Partial<Record<ExportScope, ExportSizeSource>> | null): ExportSizes {
  const measured = useRef(new WeakMap<ExportTable, Partial<Record<ExportFormat, number>>>());
  const [sizes, setSizes] = useState<ExportSizes>({});

  useEffect(() => {
    if (!sources) {
      return undefined;
    }
    let cancelled = false;
    const scopes = Object.keys(sources) as ExportScope[];
    setSizes(Object.fromEntries(scopes.map(scope => [scope, measured.current.get(sources[scope]!.table) ?? {}])));
    for (const scope of scopes) {
      const { fileName, table } = sources[scope]!;
      for (const format of FORMATS.filter(candidate => measured.current.get(table)?.[candidate] === undefined)) {
        getFileSize(table, format, fileName)
          .then(bytes => {
            measured.current.set(table, { ...measured.current.get(table), [format]: bytes });
            if (!cancelled) {
              setSizes(previous => ({ ...previous, [scope]: { ...previous[scope], [format]: bytes } }));
            }
          })
          .catch(() => undefined);
      }
    }
    return () => {
      cancelled = true;
    };
  }, [sources]);

  return sizes;
}
